import type { BackgroundMessage, BackgroundResponse } from './types';
import { MessageType } from './types';

/**
 * Send a message to the background service worker and await response
 * @param message Message to send
 * @returns Promise resolving to the response
 */
export async function sendToBackground<T = any>(
  message: BackgroundMessage
): Promise<BackgroundResponse<T>> {
  return new Promise((resolve, reject) => {
    if (!chrome?.runtime?.sendMessage) {
      reject(new Error('chrome.runtime.sendMessage not available'));
      return;
    }

    chrome.runtime.sendMessage(message, (response: BackgroundResponse<T>) => {
      if (chrome.runtime.lastError) {
        reject(new Error(chrome.runtime.lastError.message));
        return;
      }

      if (!response) {
        reject(new Error('No response from background'));
        return;
      }

      if (!response.success) {
        reject(new Error(response.error || 'Background request failed'));
        return;
      }

      resolve(response);
    });
  });
}

/**
 * Check if background service worker is available
 */
export async function isBackgroundAvailable(): Promise<boolean> {
  try {
    const response = await chrome.runtime.sendMessage({
      type: MessageType.PING,
    });
    return !!response;
  } catch {
    return false;
  }
}
